<template>
	<div
		class="flex justify-between items-center dossier-row p-2.5"
		:class="{ 'border-b': !isLast }"
	>
		<RouterLink
			tabindex="0"
			class="flex-1 min-w-0"
			:to="{
				name: 'Financiering',
				params: { dossierDisplayId: dossier.displayId },
			}"
		>
			{{ dossier.name }}
		</RouterLink>
		<div class="flex gap-3 items-center">
			<div>
				{{ formatDate(dossier.lastOpened) }}
			</div>
			<button
				class="duplicate-btn"
				:title="duplicating ? 'Bezig met kopiëren...' : 'Kopieer dossier'"
				:disabled="duplicating"
				@click.stop="handleDuplicate"
			>
				<svg
					v-if="!duplicating"
					xmlns="http://www.w3.org/2000/svg"
					width="14"
					height="14"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				>
					<rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
					<path
						d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
					></path>
				</svg>
				<span v-else class="spinner"></span>
			</button>
			<div>
				<UserProfileIcon
					v-if="dossier.dossierOwner"
					:user-id="dossier.dossierOwner"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import { formatDate } from "@/helpers/uiHelpers";
import { useDossierStore } from "@/store/useDossierStore";
import type { Row } from "@rocicorp/zero";

const props = defineProps<{
	dossier: Row["dossier"];
	isLast: boolean;
}>();

const router = useRouter();
const dossierStore = useDossierStore();
const duplicating = ref(false);

async function handleDuplicate() {
	if (duplicating.value) return;
	duplicating.value = true;

	try {
		const displayId = await dossierStore.duplicateDossier(props.dossier.id);

		if (displayId != null) {
			await router.push({
				name: "Financiering",
				params: { dossierDisplayId: displayId },
			});
		}
	} finally {
		duplicating.value = false;
	}
}
</script>

<style scoped lang="scss">
.dossier-row {
	height: 100%;
}

.dossier-row:not(.disabled):focus-within,
.dossier-row:not(.disabled):hover {
	cursor: pointer;
	outline: none;
	background: var(--background-primary-hover);
}

.duplicate-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	border-radius: 4px;
	color: var(--text-muted);
	background: transparent;
	border: none;
	cursor: pointer;
	transition: color 0.15s;

	&:hover {
		color: var(--accent-primary);
	}

	&:disabled {
		cursor: wait;
	}
}

.spinner {
	display: inline-block;
	width: 12px;
	height: 12px;
	border: 2px solid var(--text-muted, #81838c);
	border-top-color: var(--accent, #9250e9);
	border-radius: 50%;
	animation: spin 0.6s linear infinite;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}
</style>
